import { NavLink, Link } from 'react-router-dom';
import cn from 'clsx';

import { ArrowTopRightLine } from '@bizzabo/design-system';

import useTypeTracking from '../../../analytics';
import { useScrollHook, SCROLL_DOWN } from '../../../hooks/useScrollHook';
import { useIsCommunityEnabled } from 'people';
import { ShowProfileModalButton } from '../../../people/UserProfile';
import type { ExtendedNavigationProps } from '../types';
import { useStyles } from './NavigationLgMd.style';
import useVenueUrl from 'hooks/useVenueUrl/useVenueUrl';
import useAgendaUrl from 'hooks/useVenueUrl/useAgendaUrl';
import useIsEventExperienceSummitEvent from 'hooks/useIsEventExperienceSummitEvent';
import { IntegrationPointData, IntegrationPointType } from '../../../extensions/types/appExtension';
import useEventAppExtensions from '../../../hooks/useEventAppExtensions';

export const NavigationLgMd: React.FC<ExtendedNavigationProps> = ({
    eventUniqueName,
    goToHomePage,
    eventHeaderLogoUrl,
    eventName,
    eventId,
    menuCustomization,
    shouldShowAgenda,
}) => {
    const isCommunityEnabled = useIsCommunityEnabled(eventUniqueName);
    const baseUrl = useVenueUrl();
    const agendaUrl = useAgendaUrl();
    const classes = useStyles();
    const { trackClickEvent } = useTypeTracking();
    const { scrollDirection } = useScrollHook();
    const isEventExperienceSummitEvent = useIsEventExperienceSummitEvent();
    const headerClass = cn(classes.header, classes.appToolsHeader, {
        [classes.hideHeader]: scrollDirection === SCROLL_DOWN,
    });
    const appExtension = useEventAppExtensions(
        eventId,
        IntegrationPointType.VENUE_EVENT_MAIN_STAGE
    );

    const navigationText = {
        agenda: menuCustomization?.agendaText || 'Agenda',
        community: menuCustomization?.communityText || 'Community',
        moreInfo: menuCustomization?.moreInfoText || 'More Info',
    };

    return (
        <header className={headerClass}>
            <div className={classes.headerWrapper}>
                {eventHeaderLogoUrl ? (
                    <Link to={baseUrl}>
                        <div className={classes.imageWrapper}>
                            <img
                                className={classes.eventLogo}
                                src={eventHeaderLogoUrl}
                                alt={eventName}
                            />
                        </div>
                    </Link>
                ) : (
                    <div className={classes.emptyBlock} />
                )}
                <nav>
                    <ul className={classes.navigationUl}>
                        {shouldShowAgenda && (
                            <li>
                                <NavLink
                                    to={agendaUrl}
                                    className={navData =>
                                        cn(classes.link, 'nav-link', {
                                            [classes.linkActive]: navData.isActive,
                                            active: navData.isActive,
                                        })
                                    }
                                    onClick={() => {
                                        trackClickEvent({
                                            buttonType: 'GO_TO_VIA_PAGE_HEADER',
                                            subCategory: 'AGENDA_RING',
                                        });
                                    }}
                                >
                                    {navigationText.agenda}
                                </NavLink>
                            </li>
                        )}
                        {isCommunityEnabled && (
                            <li>
                                <NavLink
                                    to={`${baseUrl}/community`}
                                    className={navData =>
                                        cn(classes.link, 'nav-link', {
                                            [classes.linkActive]: navData.isActive,
                                            active: navData.isActive,
                                        })
                                    }
                                >
                                    {navigationText.community}
                                </NavLink>
                            </li>
                        )}
                        {appExtension && (
                            <li key={appExtension.id}>
                                <NavLink
                                    to={`${baseUrl}/extension/${appExtension.id}`}
                                    className={navData =>
                                        cn(classes.link, 'nav-link', {
                                            [classes.linkActive]: navData.isActive,
                                            active: navData.isActive,
                                        })
                                    }
                                >
                                    {IntegrationPointData.VENUE_EVENT_MAIN_STAGE.label}
                                </NavLink>
                            </li>
                        )}
                        <li>
                            <a className={cn(classes.link, 'nav-link')} onClick={goToHomePage}>
                                {isEventExperienceSummitEvent
                                    ? 'Summit Activity Series '
                                    : `${navigationText.moreInfo} `}
                                <ArrowTopRightLine className={classes.arrowIcon} />
                            </a>
                        </li>
                    </ul>
                </nav>
                <div />
                <div className={classes.actions}>
                    <div className={classes.userProfileWrapper}>
                        <ShowProfileModalButton eventId={eventId} />
                    </div>
                </div>
            </div>
        </header>
    );
};
